/* eslint-disable react/sort-comp */
/* eslint-disable fp/no-mutation */
// component from frame-component-library https://bitbucket.nike.com/projects/BEP/repos/frame-component-library/browse
import mapValues from 'lodash/mapValues';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { isWithinAllowedDateRange } from './Calendar';
import DatePicker from './DatePicker';

export const START = 'start';
export const END = 'end';

const getISOFormattedDates = (dates, enableRange) => (enableRange ? mapValues(dates, (d) => d && d.toISOString()) : dates.start && dates.start.toISOString());

class DatePickerContainer extends Component {
  constructor(props) {
    super(props);
    this.cancelSetDate = this.cancelSetDate.bind(this);
    this.handleOnClickOff = this.handleOnClickOff.bind(this);
    this.hydrateDatePicker = this.hydrateDatePicker.bind(this);
    this.updateEndDate = this.updateEndDate.bind(this);
    this.updateStartDate = this.updateStartDate.bind(this);

    this.updateHours = this.updateHours.bind(this);
    this.updateMinutes = this.updateMinutes.bind(this);

    this.state = {
      errorMessage: false,
      nextDateSelection: START,
    };
  }

  componentDidMount() {
    this.hydrateDatePicker(this.props);
  }

  componentDidUpdate(prevProps) {
    if (prevProps !== this.props) {
      this.hydrateDatePicker(this.props);
    }
  }

  hydrateDatePicker(props) {
    const {
      earliestDateTime, enableRange, latestDateTime, selectedDates: { end, start },
    } = props;

    const startDate = moment(start);
    const defaultDate = moment().startOf('day');
    const endDate = enableRange && end ? moment(end) : undefined;
    const earliestDate = earliestDateTime && moment(earliestDateTime);
    const latestDate = latestDateTime && moment(latestDateTime);

    const newState = {
      earliestDate,
      errorMessage: (isWithinAllowedDateRange(startDate, earliestDate, latestDate))
        || (endDate && isWithinAllowedDateRange(endDate, earliestDate, latestDate)),
      latestDate,
      originalEndDate: endDate,
      originalStartDate: startDate,
      selectedEndDate: endDate,
      selectedStartDate: startDate,
      startHours: startDate.format('H'),
      startMinutes: startDate.format('mm'),
    };

    if (enableRange) {
      newState.endHours = (endDate || defaultDate).format('H');
      newState.endMinutes = (endDate || defaultDate).format('mm');
    }

    this.setState(newState);
  }

  updateStartDate(newStartDate, errorMessage) {
    const { enableRange, enableTime, onDayClick } = this.props;
    const { startHours, startMinutes } = this.state;

    // Preserve time
    if (enableTime) {
      newStartDate.hour(startHours).minute(startMinutes);
    }

    this.setState({
      errorMessage,
      nextDateSelection: enableRange ? END : START,
      selectedEndDate: undefined,
      selectedStartDate: newStartDate,
    }, () => {
      if (onDayClick) {
        onDayClick(getISOFormattedDates({ end: this.state.selectedEndDate, start: this.state.selectedStartDate }, enableRange));
      }
    });
  }

  updateEndDate(newEndDate, errorMessage) {
    const { enableRange, enableTime, onDayClick } = this.props;
    const {
      endHours, endMinutes, selectedStartDate, startHours, startMinutes,
    } = this.state;

    // Preserve time
    if (enableTime) {
      newEndDate.hour(endHours).minute(endMinutes);
    }

    const sortedDates = {};

    // Set dates in order (start is earlier, end is later)
    if (selectedStartDate.isBefore(newEndDate)) {
      sortedDates.selectedStartDate = selectedStartDate;
      sortedDates.selectedEndDate = newEndDate;
    } else if (selectedStartDate.isSame(newEndDate, 'day') && selectedStartDate.isAfter(newEndDate)) {
      // When start time is after end time on the same day, set end time equal to start time
      newEndDate.hour(startHours).minute(startMinutes);
      sortedDates.selectedStartDate = selectedStartDate;
      sortedDates.selectedEndDate = newEndDate;
      this.setState({ endHours: startHours, endMinutes: startMinutes });
    } else {
      sortedDates.selectedStartDate = newEndDate.hour(startHours).minute(startMinutes);
      sortedDates.selectedEndDate = selectedStartDate.hour(endHours).minute(endMinutes);
    }

    this.setState({
      errorMessage,
      nextDateSelection: START,
      selectedEndDate: sortedDates.selectedEndDate,
      selectedStartDate: sortedDates.selectedStartDate,
    }, () => {
      if (onDayClick) {
        onDayClick(getISOFormattedDates({ end: this.state.selectedEndDate, start: this.state.selectedStartDate }, enableRange));
      }
    });
  }

  updateHours(hours, updateStartOrEnd, errorMessage) {
    const { enableRange } = this.props;
    const { selectedStartDate, selectedEndDate } = this.state;
    if (updateStartOrEnd === START) {
      const newStartDate = selectedStartDate.clone().hour(hours);
      this.setState({
        errorMessage: errorMessage || (enableRange && selectedEndDate && newStartDate.isAfter(selectedEndDate)),
        selectedStartDate: newStartDate,
        [`${updateStartOrEnd}Hours`]: hours,
      });
    } else {
      const newEndDate = selectedEndDate.clone().hour(hours);
      this.setState({
        errorMessage: errorMessage || newEndDate.isBefore(selectedStartDate),
        selectedEndDate: newEndDate,
        [`${updateStartOrEnd}Hours`]: hours,
      });
    }
  }

  updateMinutes(minutes, updateStartOrEnd, errorMessage) {
    const { enableRange } = this.props;
    const { selectedEndDate, selectedStartDate } = this.state;

    if (updateStartOrEnd === START) {
      const newStartDate = selectedStartDate.clone().minute(minutes);
      this.setState({
        errorMessage: errorMessage || (enableRange && selectedEndDate && newStartDate.isAfter(selectedEndDate)),
        selectedStartDate: newStartDate,
        [`${updateStartOrEnd}Minutes`]: minutes,
      });
    } else {
      const newEndDate = selectedEndDate.clone().minute(minutes);
      this.setState({
        errorMessage: errorMessage || newEndDate.isBefore(selectedStartDate),
        selectedEndDate: newEndDate,
        [`${updateStartOrEnd}Minutes`]: minutes,
      });
    }
  }

  cancelSetDate() {
    const { originalEndDate, originalStartDate } = this.state;

    this.setState({
      nextDateSelection: START,
      selectedEndDate: originalEndDate,
      selectedStartDate: originalStartDate,
    });
  }

  handleOnClickOff() {
    const { enableRange, onClickOff } = this.props;
    const { selectedEndDate, selectedStartDate } = this.state;

    const dateToSave = {
      end: selectedEndDate,
      start: selectedStartDate,
    };

    this.setState({
      nextDateSelection: START,
      originalEndDate: selectedEndDate,
      originalStartDate: selectedStartDate,
    });
    if (onClickOff) {
      onClickOff(getISOFormattedDates(dateToSave, enableRange));
    }
  }

  render() {
    const {
      className, earliestDateTime, enableOutsideDays, enableRange, enableTime,
      isDisabled, latestDateTime, paddingBottom, paddingTop, placeholder, zIndex,
    } = this.props;
    const {
      errorMessage, nextDateSelection, selectedEndDate, selectedStartDate,
    } = this.state;

    const earliestDate = earliestDateTime && moment(earliestDateTime);
    const latestDate = latestDateTime && moment(latestDateTime);
    const selectedDates = { end: selectedEndDate, start: selectedStartDate };

    return (
      <DatePicker
        className={className}
        earliestDateTime={earliestDate}
        enableOutsideDays={enableOutsideDays}
        enableRange={enableRange}
        enableTime={enableTime}
        errorMessage={errorMessage}
        handleCancelSetDate={this.cancelSetDate}
        handleOutsideClick={this.handleOnClickOff}
        handleUpdateDate={enableRange && nextDateSelection === END ? this.updateEndDate : this.updateStartDate}
        handleUpdateHours={this.updateHours}
        handleUpdateMinutes={this.updateMinutes}
        isDisabled={isDisabled}
        latestDateTime={latestDate}
        paddingBottom={paddingBottom}
        paddingTop={paddingTop}
        placeholder={placeholder}
        selectedDates={selectedDates}
        zIndex={zIndex}
      />
    );
  }
}

DatePickerContainer.propTypes = {
  className: PropTypes.string,
  earliestDateTime: PropTypes.shape(),
  enableOutsideDays: PropTypes.bool,
  enableRange: PropTypes.bool,
  enableTime: PropTypes.bool,
  isDisabled: PropTypes.bool,
  latestDateTime: PropTypes.shape(),
  onClickOff: PropTypes.func.isRequired,
  onDayClick: PropTypes.func,
  paddingBottom: PropTypes.number,
  paddingTop: PropTypes.number,
  placeholder: PropTypes.string.isRequired,
  selectedDates: PropTypes.shape({
    end: PropTypes.shape(),
    start: PropTypes.shape().isRequired,
  }).isRequired,
  zIndex: PropTypes.number,
};

DatePickerContainer.defaultProps = {
  className: '',
  earliestDateTime: undefined,
  enableOutsideDays: false,
  enableRange: false,
  enableTime: false,
  isDisabled: false,
  latestDateTime: undefined,
  onDayClick: null,
  paddingBottom: 10,
  paddingTop: 10,
  zIndex: 10,
};

DatePickerContainer.displayName = 'DatePicker';

export default DatePickerContainer;
